import { useEffect, useRef, useState } from "react";
import { generateExport, listFlags, type FlagRow, type SheetRow } from "./actions";
import { downloadCsv } from "./csv";
import { Preview } from "./SheetPanel";

// Flagged rows, shown as a table to review in the console first. Exporting is a button, not the first step: the point
// is to look at the rows and work here, not to carry them to another tool. The export is download only; sending it to
// anyone (NDC support, for example) stays a deliberate human step.

type Obj = Record<string, unknown>;
const str = (o: Obj | null | undefined, k: string) => (o && typeof o[k] === "string" ? (o[k] as string) : "");
const num = (o: Obj | null | undefined, k: string) => (o && typeof o[k] === "number" ? (o[k] as number) : null);
const money = (n: number | null) => (n == null ? "—" : n.toLocaleString(undefined, { style: "currency", currency: "USD" }));
const when = (s: string) => (s ? s.slice(0, 16).replace("T", " ") : "—");

type Col = { label: string; cell: (r: FlagRow) => string; mono?: boolean; num?: boolean; clip?: boolean };
const feed = (r: FlagRow) => str(r.flag, "source_id") || str(r.flag, "source") || r.docId.split("-")[0];
const base: Col[] = [
  { label: "Feed", cell: feed },
  { label: "SKU", cell: (r) => str(r.flag, "sku"), mono: true },
  { label: "Name", cell: (r) => str(r.product, "name") || str(r.flag, "name"), clip: true },
  { label: "Manufacturer", cell: (r) => str(r.product, "manufacturer_name"), clip: true },
];
const COLUMNS: Record<string, Col[]> = {
  status_stock_but_zero_quantity: [
    ...base,
    { label: "Category", cell: (r) => str(r.product, "category"), clip: true },
    { label: "Qty", cell: (r) => String(num(r.flag, "quantity_on_hand") ?? "—"), num: true },
    { label: "Stock status", cell: (r) => str(r.flag, "stock_status") },
    { label: "Feed price", cell: (r) => money(num(r.product, "price")), num: true },
    { label: "Flagged", cell: (r) => when(str(r.flag, "flagged_at")) },
  ],
  status_nonstock_but_positive_quantity: [
    ...base,
    { label: "Category", cell: (r) => str(r.product, "category"), clip: true },
    { label: "Qty", cell: (r) => String(num(r.flag, "quantity_on_hand") ?? "—"), num: true },
    { label: "Stock status", cell: (r) => str(r.flag, "stock_status") },
    { label: "Feed price", cell: (r) => money(num(r.product, "price")), num: true },
    { label: "Flagged", cell: (r) => when(str(r.flag, "flagged_at")) },
  ],
  price_below_wholesale_cost: [
    ...base,
    { label: "Feed price", cell: (r) => money(num(r.flag, "price")), num: true },
    { label: "Wholesale cost", cell: (r) => money(num(r.flag, "wholesale_cost")), num: true },
    { label: "Below cost by", num: true, cell: (r) => {
        const p = num(r.flag, "price"), c = num(r.flag, "wholesale_cost");
        return p == null || c == null ? "—" : `${money(c - p)} (${(((c - p) / c) * 100).toFixed(1)}%)`;
      } },
    { label: "Flagged", cell: (r) => when(str(r.flag, "flagged_at")) },
  ],
};

const PAGE_SIZES = [25, 50, 100];

export function FlaggedPanel({ issueType, label }: { issueType: string; label: string }) {
  const [rows, setRows] = useState<FlagRow[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [selected, setSelected] = useState<SheetRow | null>(null);
  const cursors = useRef<unknown[]>([undefined]);
  const request = useRef(0);
  const cols = COLUMNS[issueType] ?? base;

  const loadPage = async (n: number) => {
    const id = ++request.current;
    setLoading(true);
    const r = await listFlags(issueType, { cursor: cursors.current[n], limit: pageSize });
    if (id !== request.current) return;
    setLoading(false);
    if (!r.ok) { setError(r.error); setRows([]); setHasNext(false); return; }
    setError(null);
    setRows(r.data.rows);
    setTotal(r.data.total);
    cursors.current[n + 1] = r.data.nextCursor ?? undefined;
    cursors.current.length = n + 2;
    setHasNext(!!r.data.nextCursor);
    setPage(n);
  };
  useEffect(() => { setSelected(null); cursors.current = [undefined]; loadPage(0); }, [issueType, pageSize]);

  const exportCsv = async () => {
    setExporting(true);
    const r = await generateExport(issueType);
    setExporting(false);
    if (r.ok) downloadCsv(`hec-flags-${issueType}.csv`, r.data.csv);
    else setError(r.error);
  };

  const from = rows.length ? page * pageSize + 1 : 0;
  const to = page * pageSize + rows.length;

  return (
    <div className="sheet-page">
      <div className="sheet-top">
        <div className="sheet-title">
          <h1>{label}</h1>
          <span className="meta">
            {total == null ? "Counting…" : `${total.toLocaleString()} flagged`}. Review the rows here; click one for the full
            product. Export downloads a CSV only and sends nothing anywhere.
          </span>
        </div>
        {error && <div className="panel error sheet-error">{error}</div>}
        <div className="sheet-actions">
          <button type="button" className="sheet-export" disabled={exporting || total === 0} onClick={exportCsv}>
            {exporting ? "Preparing…" : `Export all ${total == null ? "" : total.toLocaleString() + " "}as CSV`}
          </button>
        </div>
      </div>

      <div className="sheet-scroll">
        <table className="manual-products sheet-table">
          <thead><tr>{cols.map((c) => <th key={c.label}>{c.label}</th>)}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.docId} className={selected?.docId === r.docId ? "selected" : ""}
                  onClick={() => r.product && setSelected({ docId: r.docId, fields: r.product })}>
                {cols.map((c) => {
                  const v = c.cell(r);
                  return (
                    <td key={c.label} className={[c.mono ? "mono" : "", c.num ? "num" : ""].join(" ").trim()}>
                      {c.clip ? <span className="clip" title={v}>{v}</span> : v}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && !loading && !error && <p className="meta" style={{ padding: 16 }}>Nothing is flagged here.</p>}
      </div>

      <div className="sheet-pager" role="navigation" aria-label="Pagination">
        <span className="meta">
          {loading ? "Loading…" : rows.length ? `Rows ${from.toLocaleString()}–${to.toLocaleString()}` : "No rows"}
          {total != null ? ` of ${total.toLocaleString()}` : ""} · Page {page + 1}
        </span>
        <span className="sheet-pager-buttons">
          <label>Rows per page{" "}
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <button type="button" disabled={loading || page === 0} onClick={() => loadPage(0)}>First</button>
          <button type="button" disabled={loading || page === 0} onClick={() => loadPage(page - 1)}>Previous</button>
          <button type="button" disabled={loading || !hasNext} onClick={() => loadPage(page + 1)}>Next</button>
        </span>
      </div>

      {selected && <Preview row={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
